import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, retry } from 'rxjs';
import { environment } from '../../environments/environment';
import { OffboardingRecord, CreateOffboardingRecord, OffboardingTask, ExitQuestionnaire, PublicQuestionnaire, QuestionnaireAnswer, OffboardingTaskTemplate, CreateOffboardingTaskTemplate } from '../models/offboarding.model';

@Injectable({ providedIn: 'root' })
export class OffboardingService {
  private apiUrl = `${environment.apiUrl}/Offboarding`;

  constructor(private http: HttpClient) {}

  getAllRecords(status?: string): Observable<OffboardingRecord[]> {
    if (status) {
      return this.http.get<OffboardingRecord[]>(this.apiUrl, { params: { status } }).pipe(retry(2));
    }
    return this.http.get<OffboardingRecord[]>(this.apiUrl).pipe(retry(2));
  }

  getRecordById(id: string): Observable<OffboardingRecord> {
    return this.http.get<OffboardingRecord>(`${this.apiUrl}/${id}`).pipe(retry(2));
  }

  createRecord(record: CreateOffboardingRecord): Observable<OffboardingRecord> {
    return this.http.post<OffboardingRecord>(this.apiUrl, record);
  }

  updateRecord(id: string, record: CreateOffboardingRecord): Observable<OffboardingRecord> {
    return this.http.put<OffboardingRecord>(`${this.apiUrl}/${id}`, record);
  }

  updateTasks(recordId: string, tasks: { id: string; isCompleted: boolean }[]): Observable<any> {
    return this.http.put(`${this.apiUrl}/${recordId}/tasks`, tasks, { responseType: 'text' });
  }

  getTasksByDepartment(recordId: string, department: string): Observable<OffboardingTask[]> {
    return this.http.get<OffboardingTask[]>(`${this.apiUrl}/${recordId}/tasks/${department}`).pipe(retry(2));
  }

  cancelRecord(id: string): Observable<any> {
    return this.http.put(`${this.apiUrl}/${id}/cancel`, {}, { responseType: 'text' });
  }

  activateRecord(id: string): Observable<any> {
    return this.http.put(`${this.apiUrl}/${id}/activate`, {}, { responseType: 'text' });
  }

  /** Permanently deletes a Completed or Cancelled record (admin only). */
  deleteRecord(id: string): Observable<any> {
    return this.http.delete(`${this.apiUrl}/${id}`, { responseType: 'text' });
  }

  getQuestionnaire(recordId: string): Observable<ExitQuestionnaire> {
    return this.http.get<ExitQuestionnaire>(`${this.apiUrl}/${recordId}/questionnaire`).pipe(retry(2));
  }

  updateQuestionnaire(recordId: string, answers: QuestionnaireAnswer[]): Observable<ExitQuestionnaire> {
    return this.http.put<ExitQuestionnaire>(`${this.apiUrl}/${recordId}/questionnaire`, { answers });
  }

  completeQuestionnaire(recordId: string): Observable<void> {
    return this.http.put<void>(`${this.apiUrl}/${recordId}/questionnaire/complete`, {});
  }

  // Public questionnaire (token-based, no auth)
  getPublicQuestionnaire(token: string): Observable<PublicQuestionnaire> {
    return this.http.get<PublicQuestionnaire>(`${this.apiUrl}/questionnaire/public/${token}`).pipe(retry(2));
  }

  submitPublicQuestionnaire(token: string, answers: QuestionnaireAnswer[]): Observable<PublicQuestionnaire> {
    return this.http.put<PublicQuestionnaire>(`${this.apiUrl}/questionnaire/public/${token}`, { answers });
  }

  // Attachment upload
  uploadAttachment(file: File): Observable<{ url: string; fileName: string }> {
    const formData = new FormData();
    formData.append('file', file);
    return this.http.post<{ url: string; fileName: string }>(`${this.apiUrl}/upload-attachment`, formData);
  }

  // Task Templates
  getAllTaskTemplates(): Observable<OffboardingTaskTemplate[]> {
    return this.http.get<OffboardingTaskTemplate[]>(`${this.apiUrl}/templates`).pipe(retry(2));
  }

  createTaskTemplate(template: CreateOffboardingTaskTemplate): Observable<OffboardingTaskTemplate> {
    return this.http.post<OffboardingTaskTemplate>(`${this.apiUrl}/templates`, template);
  }

  updateTaskTemplate(id: string, template: OffboardingTaskTemplate): Observable<OffboardingTaskTemplate> {
    return this.http.put<OffboardingTaskTemplate>(`${this.apiUrl}/templates/${id}`, template);
  }

  deleteTaskTemplate(id: string): Observable<any> {
    return this.http.delete(`${this.apiUrl}/templates/${id}`, { responseType: 'text' });
  }

  seedDefaultTemplates(): Observable<any> {
    return this.http.post(`${this.apiUrl}/templates/seed-defaults`, {}, { responseType: 'text' });
  }
}
